export const NOTE_TYPE_DETECT_SYSTEM = `Given this doctor speech
transcript — return ONLY one of
these exact strings, nothing else:
first_visit
follow_up
procedure
emergency

first_visit: full history being taken,
patient describing symptoms for
first time, no previous diagnosis
mentioned.

follow_up: returning patient,
known diagnosis mentioned,
checking progress or medication.

procedure: physical procedure
performed — suturing, injection,
biopsy, wound care, removal.

emergency: acute urgent complaint,
severe pain, chest pain,
difficulty breathing, immediate
treatment given.

Default to first_visit if unclear.`;

export const FIRST_VISIT_PROMPT = `You are a clinical documentation AI
for outpatient clinic doctors.
Extract from doctor speech only.
Return ONLY valid JSON.
No markdown. No explanation.

{
  "note_type": "first_visit",
  "soap": {
    "subjective": {
      "chief_complaint": "",
      "history_of_present_illness": "",
      "past_medical_history": "",
      "family_history": "",
      "social_history": "",
      "allergies": "",
      "current_medications": [
        {
          "name": "",
          "dose": "",
          "frequency": ""
        }
      ]
    },
    "objective": {
      "vitals": {
        "bp": "",
        "hr": "",
        "temp": "",
        "weight": "",
        "o2_sat": ""
      },
      "examination_findings": ""
    },
    "assessment": {
      "diagnosis": ""
    },
    "plan": {
      "medications_prescribed": [
        {
          "name": "",
          "dose": "",
          "route": "oral",
          "frequency": "",
          "duration": "",
          "instructions": "",
          "high_alert": false
        }
      ],
      "follow_up": "",
      "instructions": ""
    }
  }
}

Rules:
Write ALL clinical text values in English only.
Translate Arabic (or mixed) speech into clear English.
Do not leave Arabic script in any field.
Empty string if not mentioned.
Never fabricate information.
o2_sat: number only, no % sign (e.g. "93" not "93%").
High alert medications:
Warfarin, Insulin, Digoxin,
Heparin, Methotrexate, Lithium,
Amiodarone, Morphine, Fentanyl,
Tramadol, Codeine, any chemotherapy.
Default medication route to oral.`;

export const FOLLOW_UP_PROMPT = `You are a clinical documentation AI.
Extract from doctor speech only.
Return ONLY valid JSON.
No markdown. No explanation.

{
  "note_type": "follow_up",
  "soap": {
    "subjective": {
      "current_complaints": "",
      "condition_status": "",
      "medication_compliance": ""
    },
    "objective": {
      "vitals": {
        "bp": "",
        "hr": "",
        "temp": "",
        "weight": "",
        "o2_sat": ""
      },
      "examination_findings": ""
    },
    "assessment": {
      "diagnosis": "",
      "progress": ""
    },
    "plan": {
      "medication_changes": [
        {
          "action": "",
          "name": "",
          "dose": "",
          "route": "oral",
          "frequency": "",
          "duration": "",
          "high_alert": false
        }
      ],
      "investigations": "",
      "follow_up": "",
      "instructions": ""
    }
  }
}

Rules:
condition_status: improved /
stable / worse / empty string.
medication_changes action:
add / remove / change_dose.
Write ALL clinical text values in English only.
Translate Arabic (or mixed) speech into clear English.
Do not leave Arabic script in any field.
o2_sat: number only, no % sign (e.g. "93" not "93%").
Never fabricate information.`;

export const PROCEDURE_PROMPT = `You are a clinical documentation AI.
Extract from doctor speech only.
Return ONLY valid JSON.
No markdown. No explanation.

{
  "note_type": "procedure",
  "indication": "",
  "procedure_performed": "",
  "anesthesia_used": "",
  "site": "",
  "findings": "",
  "complications": "",
  "medications_given": [
    {
      "name": "",
      "dose": "",
      "route": "",
      "high_alert": false
    }
  ],
  "post_procedure_instructions": "",
  "follow_up": ""
}

Rules:
complications default empty string.
Write ALL clinical text values in English only.
Translate Arabic (or mixed) speech into clear English.
Do not leave Arabic script in any field.
Never fabricate information.`;

export const EMERGENCY_PROMPT = `You are a clinical documentation AI.
Extract from doctor speech only.
Return ONLY valid JSON.
No markdown. No explanation.

{
  "note_type": "emergency",
  "chief_complaint": "",
  "onset": "",
  "severity": "",
  "associated_symptoms": "",
  "vitals": {
    "bp": "",
    "hr": "",
    "temp": "",
    "o2_sat": "",
    "rr": ""
  },
  "examination_findings": "",
  "diagnosis": "",
  "immediate_treatment": "",
  "medications_given": [
    {
      "name": "",
      "dose": "",
      "route": "",
      "high_alert": false
    }
  ],
  "disposition": "",
  "referral": ""
}

Rules:
severity: mild / moderate /
severe / empty string.
disposition: sent home / admitted /
referred / transferred / empty string.
Write ALL clinical text values in English only.
Translate Arabic (or mixed) speech into clear English.
Do not leave Arabic script in any field.
o2_sat: number only, no % sign (e.g. "93" not "93%").
Never fabricate information.`;

export const DOCUMENTS_DETECT_PROMPT = `Detect if doctor mentioned any
of the following.
Return ONLY valid JSON.
No markdown. No explanation.

{
  "sick_leave": {
    "needed": false,
    "diagnosis": "",
    "days": 0
  },
  "referral": {
    "needed": false,
    "specialty": "",
    "reason": "",
    "urgency": "routine"
  },
  "lab_order": {
    "needed": false,
    "tests": [],
    "indication": "",
    "urgency": "routine"
  },
  "radiology_request": {
    "needed": false,
    "investigation": "",
    "body_part": "",
    "indication": "",
    "urgency": "routine"
  }
}

Set needed: true only if
explicitly mentioned by doctor.
urgency: routine or urgent.
Write ALL text fields in English only.
Translate Arabic speech into English.
Do not leave Arabic script in any field.

radiology_request is for:
xray, ultrasound, CT, MRI only.
ECG, echo, EKG, Holter, cardiac monitoring
go in lab_order tests array.
Never put ECG (or EKG) in radiology_request.`;

export const NOTE_PROMPTS = {
  first_visit: FIRST_VISIT_PROMPT,
  follow_up: FOLLOW_UP_PROMPT,
  procedure: PROCEDURE_PROMPT,
  emergency: EMERGENCY_PROMPT,
};

/**
 * Insert patient history context into a note-generation system prompt
 * before the extraction / JSON instructions.
 */
export function prependPatientHistoryContext(systemPrompt, previousVisitsContext) {
  const context = String(previousVisitsContext || '').trim()
    || 'No previous visits for this patient.';
  const historyBlock = `PATIENT HISTORY CONTEXT
(do not copy this directly —
use it to inform the note):

${context}

Use this context to:
- Reference previous diagnoses
  when relevant.
- Detect medication changes
  from previous prescriptions.
- Note improvement or
  deterioration from last visit.
- Avoid repeating history
  already documented.
- Flag if a new medication
  conflicts with previous ones.

`;

  const marker = 'Extract from doctor speech only.';
  if (systemPrompt.includes(marker)) {
    return systemPrompt.replace(marker, `${historyBlock}${marker}`);
  }
  const nl = systemPrompt.indexOf('\n');
  if (nl === -1) return `${systemPrompt}\n\n${historyBlock}`;
  return `${systemPrompt.slice(0, nl + 1)}\n${historyBlock}${systemPrompt.slice(nl + 1)}`;
}

export const HIGH_ALERT_MEDS = [
  'warfarin',
  'insulin',
  'digoxin',
  'heparin',
  'methotrexate',
  'lithium',
  'amiodarone',
  'morphine',
  'fentanyl',
  'tramadol',
  'codeine',
];

export const SPECIALTIES = [
  'General Practice',
  'Internal Medicine',
  'Pediatrics',
  'Neurology',
  'Cardiology',
  'Gynecology',
  'Dermatology',
  'Orthopedics',
  'Other',
];

export const COUNTRIES = [
  'Jordan',
  'Palestine',
  'Saudi Arabia',
  'UAE',
  'Egypt',
  'Lebanon',
  'Iraq',
  'Other',
];
